import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { LpHeader, LpFooter, IsThisYou, LpCtaBlock, useScrollFade, fadeStyle, useLpTracking } from './LpShared';
import { trackEvent } from '../analytics';

const PAGE = '/lp/enterprise-coo';

export default function LpEnterpriseCOO() {
  const navigate = useNavigate();
  useLpTracking(PAGE);
  const costSection = useScrollFade();
  const solveSection = useScrollFade();
  const videoSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <LpHeader ctaLabel="Request Enterprise Briefing" ctaPath="/contact" pagePath={PAGE} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', minHeight: { xs: 520, md: 680 }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box component="img" src="/one-vs-many.png" alt="Multi-site monitoring" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,10,18,0.97) 0%, rgba(10,10,18,0.7) 60%, rgba(10,10,18,0.88) 100%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(140,82,255,0.1)', border: '1px solid rgba(140,82,255,0.3)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8c52ff' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#8c52ff', letterSpacing: '0.1em', textTransform: 'uppercase' }}>For Enterprise Operators</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.4rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3 }}>
            Your brand is only as safe<br />
            <Box component="span" sx={{ color: '#8c52ff' }}>as your worst location.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 580, lineHeight: 1.65, mb: 5 }}>
            You have multiple locations. You can't be everywhere. And you've already learned that one incident at one facility can take down the whole brand. SMCLA gives you visibility across every room, every location — in real time.
          </Typography>
          <Button variant="contained" size="large" onClick={() => { trackEvent('cta_click', 'Request Enterprise Briefing', PAGE, { location: 'lp_hero' }); navigate('/contact'); }} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.3)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Request an Enterprise Briefing →
          </Button>
        </Container>
      </Box>

      {/* ── Is this you? ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box component="img" src="/ai-people-tech.png" alt="AI monitoring" sx={{ width: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.07)', display: 'block', opacity: 0.85 }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <IsThisYou items={[
                "You can't physically visit every facility every week — and something always happens when you're not there",
                "One manager failed to report something. You found out three months later.",
                "Your board has started asking about child safety protocols at the portfolio level",
                "You've been through one incident. You know how fast media and lawyers move.",
              ]} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Cost of inaction ── */}
      <Box ref={costSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(costSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The portfolio risk you're carrying</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            One location can collapse the whole brand.
          </Typography>
          <Grid container spacing={3}>
            {[
              { num: '51', label: 'Criminal counts at one Colorado preschool', sub: '2025. One teacher. One location. The brand didn\'t survive the press cycle.', color: '#ff6b6b' },
              { num: '$16M', label: 'Settlement from one SC location', sub: 'One death. One location. Enrollment fell across the chain for 12 months.', color: '#ff9f43' },
              { num: '3–5', label: 'Locations typically close after one viral incident', sub: 'Enrollment collapses regionally. Parents don\'t distinguish between locations.', color: '#8c52ff' },
            ].map(({ num, label, sub, color }) => (
              <Grid key={num} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color, lineHeight: 1, mb: 1 }}>{num}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How SMCLA solves it ── */}
      <Box ref={solveSection.ref} sx={{ py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(solveSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>What changes with SMCLA</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            You know before your managers do.
          </Typography>
          <Stack spacing={2.5}>
            {[
              { num: '1', title: 'One dashboard. Every location. Real-time.', body: 'Alerts from any facility surface in one place — yours. You know about a behavioral event at your Rochester location before the site manager files a report. You see the pattern before it becomes a crisis.' },
              { num: '2', title: 'Pattern detection identifies drift weeks before an incident', body: 'A worker\'s behavior doesn\'t change overnight. SMCLA tracks vocal tone shifts, interaction patterns, and escalating responses across sessions — flagging behavioral drift before a single incident reaches severity.' },
              { num: '3', title: 'Full-facility reconstruction in one click', body: 'When an alert is confirmed, SMCLA locks the 24-hour buffer from every camera in that facility — not just the one that triggered. You have the full story, not a fragment, before anyone has had a chance to tell you their version.' },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' }, transition: 'border-color 0.2s' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Video proof ── */}
      <Box ref={videoSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(videoSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2, textAlign: 'center' }}>AI detection in action</Typography>
          <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.15)' }}>
            <video src="/Daycare_Video_with_AI_Summary.mp4" controls muted playsInline style={{ width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' }}>
              Your browser does not support embedded videos.
            </video>
          </Box>
        </Container>
      </Box>

      <LpCtaBlock
        headline="Built for operators with multiple locations."
        sub="Book an enterprise briefing — we'll cover multi-site dashboards, API integration, legal-grade evidence packaging, and pricing at scale."
        ctaLabel="Request an Enterprise Briefing"
        ctaPath="/contact"
        pagePath={PAGE}
      />
      <LpFooter />
    </Box>
  );
}
